import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Radio,
  Shield,
  ShieldCheck,
  Flame,
  Heart,
  Siren,
  Globe,
  ArrowLeft,
  MapPin,
  Share2,
  ExternalLink,
  ChevronRight,
  Loader2,
  Copy,
  Check,
  Twitter,
  Facebook
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Service type icons and colors
const SERVICE_CONFIG = {
  police: { icon: Siren, color: 'text-blue-400', bg: 'bg-blue-500/20' },
  fire: { icon: Flame, color: 'text-orange-400', bg: 'bg-orange-500/20' },
  ems: { icon: Heart, color: 'text-red-400', bg: 'bg-red-500/20' },
  dispatch: { icon: Radio, color: 'text-purple-400', bg: 'bg-purple-500/20' },
  interop: { icon: Globe, color: 'text-emerald-400', bg: 'bg-emerald-500/20' },
  other: { icon: Radio, color: 'text-slate-400', bg: 'bg-slate-500/20' }
};

// Status badges
const STATUS_CONFIG = {
  official: { color: 'bg-emerald-600', text: 'Official' },
  verified: { color: 'bg-blue-600', text: 'Verified' },
  likely_valid: { color: 'bg-teal-600', text: 'Likely Valid' },
  unverified: { color: 'bg-amber-600', text: 'Unverified' }
};

// US States map
const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia'
};

// Frequency card for SEO pages
const FrequencyCard = ({ freq }) => {
  const [copied, setCopied] = useState(false);
  const config = SERVICE_CONFIG[freq.service_type] || SERVICE_CONFIG.other;
  const ServiceIcon = config.icon;
  const status = STATUS_CONFIG[freq.verification_status] || STATUS_CONFIG.unverified;
  
  const copyFreq = () => {
    navigator.clipboard.writeText(freq.frequency.toFixed(4));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 hover:border-teal-500/30 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <ServiceIcon className={`w-4 h-4 ${config.color}`} />
            <button 
              onClick={copyFreq}
              className="font-mono text-lg font-bold text-teal-400 hover:text-teal-300 flex items-center gap-1"
              title="Click to copy"
            >
              {freq.frequency.toFixed(4)} MHz
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3 opacity-50" />}
            </button>
            {freq.alpha_tag && (
              <span className="text-xs text-slate-500 font-mono">[{freq.alpha_tag}]</span>
            )}
          </div>
          <p className="text-slate-300 mb-2">{freq.description}</p>
          <div className="flex flex-wrap gap-1.5 text-xs">
            {freq.agency && (
              <span className="px-2 py-0.5 bg-slate-700 rounded text-slate-300">{freq.agency}</span>
            )}
            {freq.mode && (
              <span className="px-2 py-0.5 bg-slate-700/50 rounded text-slate-400">{freq.mode}</span>
            )}
            {freq.tone && (
              <span className="px-2 py-0.5 bg-amber-900/30 rounded text-amber-400">{freq.tone}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Badge className={`${status.color} text-white text-xs`}>
            {status.text}
          </Badge>
          <span className={`text-xs ${freq.confidence_score >= 80 ? 'text-emerald-400' : 'text-amber-400'}`}>
            {freq.confidence_score}% confidence
          </span>
        </div>
      </div>
    </div>
  );
};

// Share buttons component
const ShareButtons = ({ title, url }) => {
  const shareTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`, '_blank');
  };
  
  const shareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
  };
  
  const copyLink = () => {
    navigator.clipboard.writeText(url);
  };
  
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-slate-400">Share:</span>
      <Button size="sm" variant="ghost" onClick={shareTwitter} className="h-8 w-8 p-0">
        <Twitter className="w-4 h-4" />
      </Button>
      <Button size="sm" variant="ghost" onClick={shareFacebook} className="h-8 w-8 p-0">
        <Facebook className="w-4 h-4" />
      </Button>
      <Button size="sm" variant="ghost" onClick={copyLink} className="h-8 w-8 p-0">
        <Copy className="w-4 h-4" />
      </Button>
    </div>
  );
};

// State frequencies page (e.g., /frequencies/ca)
export const StateFrequenciesPage = () => {
  const { stateCode } = useParams();
  const { theme } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const stateUpper = stateCode?.toUpperCase();
  const stateName = STATE_NAMES[stateUpper] || stateCode;
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/frequencies/state/${stateUpper}`);
        if (!res.ok) throw new Error('State not found');
        const json = await res.json();
        setData(json);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (stateUpper) fetchData();
  }, [stateUpper]);
  
  // Group frequencies by city
  const byCity = data?.frequencies?.reduce((acc, freq) => {
    const city = freq.city || 'Statewide';
    if (!acc[city]) acc[city] = [];
    acc[city].push(freq);
    return acc;
  }, {}) || {};
  
  const cities = Object.keys(byCity).sort();
  const pageUrl = `https://veriwatch.io/frequencies/${stateCode}`;
  const pageTitle = `${stateName} Police, Fire & EMS Scanner Frequencies | VeriWatch`;
  const pageDesc = `Free ${stateName} scanner frequencies for police, fire, and EMS. ${data?.total || 0} verified frequencies including ${cities.filter(c => c !== 'Statewide').slice(0, 3).join(', ')}. Updated 2026.`;
  
  // JSON-LD structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "name": `${stateName} Public Safety Radio Frequencies`,
    "description": pageDesc,
    "url": pageUrl,
    "provider": {
      "@type": "Organization",
      "name": "VeriWatch",
      "url": "https://veriwatch.io"
    },
    "license": "https://creativecommons.org/publicdomain/zero/1.0/",
    "isAccessibleForFree": true,
    "spatialCoverage": {
      "@type": "Place",
      "name": stateName
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-white">
        <h1 className="text-2xl font-bold mb-4">State Not Found</h1>
        <Link to="/frequencies">
          <Button>Back to Frequency Database</Button>
        </Link>
      </div>
    );
  }
  
  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <link rel="canonical" href={pageUrl} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-slate-950 text-white">
        {/* Breadcrumb navigation */}
        <div className="border-b border-slate-800 bg-slate-900/50">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <nav className="flex items-center gap-2 text-sm">
                <Link to="/" className="text-slate-400 hover:text-white">Home</Link>
                <ChevronRight className="w-4 h-4 text-slate-600" />
                <Link to="/frequencies" className="text-slate-400 hover:text-white">Frequencies</Link>
                <ChevronRight className="w-4 h-4 text-slate-600" />
                <span className="text-teal-400">{stateName}</span>
              </nav>
              <ShareButtons title={pageTitle} url={pageUrl} />
            </div>
          </div>
        </div>
        
        {/* Hero section */}
        <div className="bg-gradient-to-b from-slate-900 to-slate-950 border-b border-slate-800">
          <div className="max-w-6xl mx-auto px-4 py-12">
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="w-8 h-8 text-teal-400" />
              <h1 className="text-3xl md:text-4xl font-bold">
                {stateName} Scanner Frequencies
              </h1>
            </div>
            <p className="text-xl text-slate-400 mb-6">
              Police, Fire & EMS radio frequencies for {stateName}
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="bg-slate-800/50 rounded-lg px-4 py-2">
                <span className="text-2xl font-bold text-teal-400">{data?.total || 0}</span>
                <span className="text-slate-400 ml-2">Frequencies</span>
              </div>
              <div className="bg-slate-800/50 rounded-lg px-4 py-2">
                <span className="text-2xl font-bold text-blue-400">{cities.filter(c => c !== 'Statewide').length}</span>
                <span className="text-slate-400 ml-2">Cities</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Main content */}
        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* City links */}
          {cities.length > 1 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-3 text-slate-300">Jump to City:</h2>
              <div className="flex flex-wrap gap-2">
                {cities.filter(c => c !== 'Statewide').map(city => (
                  <Link 
                    key={city}
                    to={`/frequencies/${stateCode}/${city.toLowerCase().replace(/\s+/g, '-')}`}
                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm transition-colors"
                  >
                    {city} ({byCity[city].length})
                  </Link>
                ))}
              </div>
            </div>
          )}
          
          {/* Frequencies by city */}
          {cities.map(city => (
            <div key={city} className="mb-10" id={city.toLowerCase().replace(/\s+/g, '-')}>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                {city === 'Statewide' ? (
                  <Globe className="w-5 h-5 text-emerald-400" />
                ) : (
                  <MapPin className="w-5 h-5 text-teal-400" />
                )}
                {city === 'Statewide' ? `${stateName} Statewide Frequencies` : `${city}, ${stateUpper}`}
              </h2>
              <div className="grid gap-3">
                {byCity[city].map((freq, idx) => (
                  <FrequencyCard key={`${freq.frequency}-${idx}`} freq={freq} />
                ))}
              </div>
            </div>
          ))}
          
          {/* National interop reminder */}
          <div className="mt-12 p-6 bg-emerald-900/20 border border-emerald-800 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
              <h3 className="font-semibold text-emerald-300">Don't Forget National Channels</h3>
            </div>
            <p className="text-emerald-200/70 mb-4">
              NIFOG national interoperability channels work in every state for emergency communications.
            </p>
            <Link to="/frequencies">
              <Button variant="outline" className="border-emerald-600 text-emerald-400 hover:bg-emerald-900/30">
                View National Channels
              </Button>
            </Link>
          </div>
          
          {/* SEO content */}
          <div className="mt-12 prose prose-invert max-w-none">
            <h2>About {stateName} Scanner Frequencies</h2>
            <p>
              This page contains verified scanner frequencies for police departments, fire departments, 
              and emergency medical services (EMS) throughout {stateName}. All frequencies are sourced 
              from the FCC Universal Licensing System (ULS) and verified by the VeriWatch community.
            </p>
            <p>
              Scanner frequencies allow you to monitor public safety communications in your area. 
              This can be valuable for situational awareness during emergencies, severe weather events, 
              or other critical incidents.
            </p>
            <h3>How to Use These Frequencies</h3>
            <ul>
              <li>Program frequencies into your scanner or SDR (Software Defined Radio)</li>
              <li>Note the mode (FM, NFM, P25) - digital modes require compatible equipment</li>
              <li>PL/CTCSS tones may be required to hear transmissions clearly</li>
              <li>Frequencies marked as "Official" or "Verified" have the highest reliability</li>
            </ul>
            <p>
              <strong>Disclaimer:</strong> Listening to police scanners is legal in most jurisdictions, 
              but using scanner information to interfere with emergency services is illegal. 
              Always use this information responsibly.
            </p>
          </div>
        </div>
        
        {/* Footer */}
        <footer className="border-t border-slate-800 bg-slate-900/50 mt-12">
          <div className="max-w-6xl mx-auto px-4 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Radio className="w-5 h-5 text-teal-400" />
                <span className="font-bold">VeriWatch</span>
                <span className="text-slate-400">- Free Scanner Frequencies</span>
              </div>
              <div className="flex gap-4 text-sm text-slate-400">
                <Link to="/frequencies" className="hover:text-white">All Frequencies</Link>
                <Link to="/" className="hover:text-white">Home</Link>
                <Link to="/dashboard" className="hover:text-white">Dashboard</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

// City frequencies page (e.g., /frequencies/ca/los-angeles)
export const CityFrequenciesPage = () => {
  const { stateCode, citySlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const stateUpper = stateCode?.toUpperCase();
  const stateName = STATE_NAMES[stateUpper] || stateCode;
  const cityName = citySlug?.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/frequencies/city/${stateUpper}/${encodeURIComponent(cityName)}`);
        if (!res.ok) throw new Error('City not found');
        const json = await res.json();
        setData(json);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (stateUpper && cityName) fetchData();
  }, [stateUpper, cityName]);
  
  // Group by service type
  const byService = data?.frequencies?.reduce((acc, freq) => {
    const svc = freq.service_type || 'other';
    if (!acc[svc]) acc[svc] = [];
    acc[svc].push(freq);
    return acc;
  }, {}) || {};
  
  const pageUrl = `https://veriwatch.io/frequencies/${stateCode}/${citySlug}`;
  const pageTitle = `${cityName}, ${stateUpper} Police & Fire Scanner Frequencies | VeriWatch`;
  const pageDesc = `Free ${cityName}, ${stateName} scanner frequencies. ${data?.total || 0} verified police, fire, and EMS frequencies. Program your scanner today.`;
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "name": `${cityName}, ${stateName} Public Safety Radio Frequencies`,
    "description": pageDesc,
    "url": pageUrl,
    "provider": {
      "@type": "Organization",
      "name": "VeriWatch",
      "url": "https://veriwatch.io"
    },
    "license": "https://creativecommons.org/publicdomain/zero/1.0/",
    "isAccessibleForFree": true,
    "spatialCoverage": {
      "@type": "Place",
      "name": `${cityName}, ${stateName}`
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
      </div>
    );
  }
  
  if (error || !data?.frequencies?.length) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-white">
        <h1 className="text-2xl font-bold mb-4">City Not Found</h1>
        <p className="text-slate-400 mb-6">We don't have frequencies for {cityName} yet.</p>
        <div className="flex gap-4">
          <Link to={`/frequencies/${stateCode}`}>
            <Button variant="outline">View {stateName} Frequencies</Button>
          </Link>
          <Link to="/frequencies">
            <Button>Submit a Frequency</Button>
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={pageUrl} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-slate-950 text-white">
        {/* Breadcrumb */}
        <div className="border-b border-slate-800 bg-slate-900/50">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <nav className="flex items-center gap-2 text-sm">
                <Link to="/" className="text-slate-400 hover:text-white">Home</Link>
                <ChevronRight className="w-4 h-4 text-slate-600" />
                <Link to="/frequencies" className="text-slate-400 hover:text-white">Frequencies</Link>
                <ChevronRight className="w-4 h-4 text-slate-600" />
                <Link to={`/frequencies/${stateCode}`} className="text-slate-400 hover:text-white">{stateName}</Link>
                <ChevronRight className="w-4 h-4 text-slate-600" />
                <span className="text-teal-400">{cityName}</span>
              </nav>
              <ShareButtons title={pageTitle} url={pageUrl} />
            </div>
          </div>
        </div>
        
        {/* Hero */}
        <div className="bg-gradient-to-b from-slate-900 to-slate-950 border-b border-slate-800">
          <div className="max-w-6xl mx-auto px-4 py-12">
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="w-8 h-8 text-teal-400" />
              <h1 className="text-3xl md:text-4xl font-bold">
                {cityName}, {stateUpper} Scanner Frequencies
              </h1>
            </div>
            <p className="text-xl text-slate-400 mb-6">
              Police, Fire & EMS radio frequencies for {cityName}
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="bg-slate-800/50 rounded-lg px-4 py-2">
                <span className="text-2xl font-bold text-teal-400">{data?.total || 0}</span>
                <span className="text-slate-400 ml-2">Frequencies</span>
              </div>
              {Object.entries(byService).map(([svc, freqs]) => {
                const config = SERVICE_CONFIG[svc] || SERVICE_CONFIG.other;
                const Icon = config.icon;
                return (
                  <div key={svc} className={`${config.bg} rounded-lg px-4 py-2 flex items-center gap-2`}>
                    <Icon className={`w-4 h-4 ${config.color}`} />
                    <span className="text-white">{freqs.length} {svc}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Frequencies */}
        <div className="max-w-6xl mx-auto px-4 py-8">
          {['police', 'fire', 'ems', 'dispatch', 'interop', 'other'].map(svc => {
            const freqs = byService[svc];
            if (!freqs?.length) return null;
            const config = SERVICE_CONFIG[svc];
            const Icon = config.icon;
            const label = svc.charAt(0).toUpperCase() + svc.slice(1);
            
            return (
              <div key={svc} className="mb-10">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Icon className={`w-5 h-5 ${config.color}`} />
                  {label} Frequencies
                </h2>
                <div className="grid gap-3">
                  {freqs.map((freq, idx) => (
                    <FrequencyCard key={`${freq.frequency}-${idx}`} freq={freq} />
                  ))}
                </div>
              </div>
            );
          })}
          
          {/* Back to state */}
          <div className="mt-8">
            <Link to={`/frequencies/${stateCode}`}>
              <Button variant="outline" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                View All {stateName} Frequencies
              </Button>
            </Link>
          </div>
          
          {/* SEO content */}
          <div className="mt-12 prose prose-invert max-w-none">
            <h2>About {cityName} Scanner Frequencies</h2>
            <p>
              This page lists verified scanner frequencies for {cityName}, {stateName}. 
              These frequencies cover local police departments, fire departments, and EMS services.
              All data is sourced from the FCC and verified by the VeriWatch community.
            </p>
            <h3>Programming Your Scanner</h3>
            <p>
              To listen to {cityName} emergency services, program these frequencies into your 
              scanner or SDR device. Make sure your equipment supports the listed mode (FM, NFM, or P25).
              Digital P25 frequencies require a scanner with P25 decoding capability.
            </p>
          </div>
        </div>
        
        {/* Footer */}
        <footer className="border-t border-slate-800 bg-slate-900/50 mt-12">
          <div className="max-w-6xl mx-auto px-4 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Radio className="w-5 h-5 text-teal-400" />
                <span className="font-bold">VeriWatch</span>
              </div>
              <div className="flex gap-4 text-sm text-slate-400">
                <Link to="/frequencies" className="hover:text-white">All Frequencies</Link>
                <Link to={`/frequencies/${stateCode}`} className="hover:text-white">{stateName}</Link>
                <Link to="/" className="hover:text-white">Home</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default StateFrequenciesPage;
